/**
 * `entities/project-file` API surface.
 *
 * Exposes:
 *   - Service functions for READING files (single, versions, list).
 *   - `*Query` queryOptions helpers for TanStack consumers, so reads flow
 *     through queryOptions and honour the cache-key contract.
 *
 * Mutations (upload orchestration, delete, status update) live in
 * `features/upload-project-file/` and `features/review-project-file/`.
 *
 * Migrated from `features/projects/api/projectFiles.api.ts`.
 */
import {
  DraftProjectsService,
  ProjectFilesService,
  type CheckNameConflictsResponseDto,
  type RequestUploadDto,
  type RequestUploadResponseDto,
  type RequestVersionUploadResponseDto,
} from '@inference.engine/workspace-api-client/web';
import { queryOptions } from '@tanstack/react-query';
import { workspaceHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';
import type {
  ProjectFile,
  ProjectFilesListQuery,
  ProjectFilesResponse,
  ProjectFileVersion,
} from '../model/types';
import { PROJECT_FILE_QUERY_KEYS } from './query-keys';

// ──────────────────────────── Service functions ────────────────────────────

export const getProjectFile = async (
  platformProjectId: string,
  fileId: string,
): Promise<ProjectFile> => {
  const response = await ProjectFilesService.getFile({
    client: workspaceHttp,
    path: { platformProjectId, fileId },
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  if (!response.data) {
    throw new Error('Failed to get project file');
  }

  return response.data;
};

export const getProjectFileVersions = async (
  platformProjectId: string,
  fileId: string,
): Promise<ProjectFileVersion[]> => {
  const response = await ProjectFilesService.listVersions({
    client: workspaceHttp,
    path: { platformProjectId, fileId },
  });

  if (!response.data) {
    throw new Error('Failed to list file versions');
  }

  return response.data;
};

export const listProjectFiles = async (
  platformProjectId: string,
  params?: ProjectFilesListQuery,
): Promise<ProjectFilesResponse> => {
  const response = await ProjectFilesService.listFiles({
    client: workspaceHttp,
    path: { platformProjectId },
    query: params,
  });

  if (!response.data) {
    throw new Error('Failed to list project files');
  }

  return response.data;
};

interface RequestUploadParams {
  fileName: string;
  fileSize: number;
  contentType: string;
  folderId?: string;
  purpose?: 'brief' | 'reviewable' | 'reference' | 'artifact';
}

export const requestProjectFileUpload = async (
  platformProjectId: string,
  params: RequestUploadParams,
): Promise<RequestUploadResponseDto> => {
  const response = await ProjectFilesService.requestUpload({
    client: workspaceHttp,
    path: { platformProjectId },
    body: params as RequestUploadDto,
  });

  if (!response.data) {
    throw new Error('Failed to request file upload URL');
  }

  return response.data;
};

/**
 * Draft-project variant of {@link requestProjectFileUpload}. Targets the
 * workspace-admin-gated `projects/drafts/:id/files/request-upload` route used by
 * the create-project wizard, where no published project exists yet.
 */
export const requestDraftProjectFileUpload = async (
  draftProjectId: string,
  params: RequestUploadParams,
): Promise<RequestUploadResponseDto> => {
  const response = await DraftProjectsService.requestUpload({
    client: workspaceHttp,
    path: { id: draftProjectId },
    body: params as RequestUploadDto,
  });

  if (!response.data) {
    throw new Error('Failed to request draft file upload URL');
  }

  return response.data;
};

/**
 * Pre-upload conflict check. Asks the server which of `fileNames` already
 * exist in the target folder (root when folderId is null/omitted). Returns one
 * entry per queried name with status 'available' | 'conflicted'
 */
export const checkProjectFileNameConflicts = async (
  platformProjectId: string,
  body: { fileNames: string[]; folderId?: string | null },
): Promise<CheckNameConflictsResponseDto> => {
  const response = await ProjectFilesService.checkNameConflicts({
    client: workspaceHttp,
    path: { platformProjectId },
    body,
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  if (!response.data) {
    throw new Error('Failed to check file name conflicts');
  }

  return response.data;
};

interface RequestVersionUploadParams {
  fileSize: number;
  contentType: string;
}

export const requestProjectFileVersionUpload = async (
  platformProjectId: string,
  fileId: string,
  params: RequestVersionUploadParams,
): Promise<RequestVersionUploadResponseDto> => {
  const response = await ProjectFilesService.requestVersionUpload({
    client: workspaceHttp,
    path: { platformProjectId, fileId },
    body: params,
  });

  if (!response.data) {
    throw new Error('Failed to request file version upload URL');
  }

  return response.data;
};

export const confirmProjectFileUpload = async (
  platformProjectId: string,
  fileId: string,
  versionId: string,
): Promise<void> => {
  await ProjectFilesService.confirmUpload({
    client: workspaceHttp,
    path: { platformProjectId, fileId, versionId },
  });
};

/**
 * Draft-project variant of {@link confirmProjectFileUpload}. Targets the
 * workspace-admin-gated `projects/drafts/:id/files/:fileId/versions/:versionId/confirm`
 * route used by the create-project wizard.
 */
export const confirmDraftProjectFileUpload = async (
  draftProjectId: string,
  fileId: string,
  versionId: string,
): Promise<void> => {
  await DraftProjectsService.confirmUpload({
    client: workspaceHttp,
    path: { id: draftProjectId, fileId, versionId },
  });
};

/**
 * Hard-deletes the underlying `project_file` row.
 *
 * Suppresses the global error-notification interceptor so callers (e.g. the
 * Library page) can own success/error toasts uniformly alongside the detach
 * flow. The page-level toast is the right granularity here: a 404 from an
 * already-deleted upload row should surface specific copy, not the generic
 * problem-details default.
 */
export const deleteProjectFile = async (
  platformProjectId: string,
  fileId: string,
): Promise<void> => {
  await ProjectFilesService.deleteFile({
    client: workspaceHttp,
    path: { platformProjectId, fileId },
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });
};

// ──────────────────────────── queryOptions helpers ─────────────────────────

export const projectFileQuery = (
  platformProjectId: string | undefined,
  fileId: string | undefined,
) =>
  queryOptions({
    queryKey: PROJECT_FILE_QUERY_KEYS.detail(platformProjectId!, fileId!),
    queryFn: () => getProjectFile(platformProjectId!, fileId!),
    enabled: !!platformProjectId && !!fileId,
    retry: 2,
    retryDelay: 500,
  });

export const projectFileVersionsQuery = (
  platformProjectId: string | undefined,
  fileId: string | undefined,
) =>
  queryOptions({
    queryKey: PROJECT_FILE_QUERY_KEYS.versions(platformProjectId!, fileId!),
    queryFn: () => getProjectFileVersions(platformProjectId!, fileId!),
    enabled: !!platformProjectId && !!fileId,
  });
